<template>
  <div
    class="relative flex flex-grow items-center justify-between rounded-md border-2 border-transparent bg-off-white p-0"
    :class="{ activeButton: show && checked }"
  >
    <div
      v-if="highlightBox"
      class="absolute right-0 top-0 rounded-bl-md rounded-tr-sm bg-yellow-500 px-2 py-1 text-xs font-semibold text-white"
    >
      {{ highlightText }}
    </div>
    <div class="inline-flex flex-grow">
      <label
        class="container"
        :class="{ 'text-dark-gray': show && checked }"
      >
        <slot />
        <section
          v-if="term === 'delta'"
          class="break-words"
        >
          <div class="mt-4 flex flex-col gap-2">
            <p class="text-sm font-medium text-neutral-subtle">
              <span>Factoring powered by Delta&#x2122;</span> <br />
              <span class="text-xs font-normal"
                >Sign up and get paid in minutes (1.5-2.5% fees)*</span
              >
            </p>
            <p class="text-xs font-normal italic text-neutral-subtlest">
              I’m opting into 2-Day Quick Pay until my factoring application is
              approved and assigned.
            </p>
          </div>
        </section>
        <section
          v-else-if="term === 'load_pay'"
          class="break-words"
        >
          <div class="flex flex-col gap-2">
            <p class="text-sm font-medium text-neutral-subtle">
              LoadPay&#x2122;
            </p>

            <p class="font-normal">
              Sign up for a bank account* that helps you get paid in minutes.
            </p>
          </div>
        </section>
        <div
          v-else
          class="flex w-full justify-between"
        >
          <div class="flex flex-col justify-center">
            <p
              class="text-1md"
              :class="{ 'text-dark-gray': checked, [customClass]: customClass }"
            >
              {{ item }}
            </p>
            <p
              v-if="!!subtext.length"
              class="text-xxs leading-3"
              :class="
                checked
                  ? `text-dark-gray ${subtextClass}`
                  : `text-body-color ${subtextClass}`
              "
              style="margin-top: 3px; margin-bottom: 3px"
              v-html="subtext"
            />
          </div>
          <div
            v-if="apm?.length > 0"
            class="flex items-center"
          >
            <div
              class="flex-row-at-400px items-center-at-400px relative flex justify-center gap-1"
              style="top: -1.5px"
            >
              <div
                v-for="m in formatAPM"
                :key="m"
                class=""
              >
                <span
                  class="rounded-md border px-1 py-0.5 text-2xs uppercase leading-none"
                  :class="[
                    checked
                      ? 'border-light-gray bg-light-gray text-dark-gray'
                      : 'border-gray-over-dark text-gray-over-dark',
                  ]"
                >
                  {{ formatAllowedPaymentMethod(m) }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <input
          type="radio"
          :value="value"
          :name="name"
          :checked="show && checked"
          :disabled="disabled"
          @click="handleClick"
        />
        <span class="checkmark" />
      </label>
    </div>
    <div
      v-if="term === 'quick_pay' || term === 'ach_deposit'"
      class="relative mr-3 flex items-center justify-center gap-1"
      style="top: -3px"
    >
      <div>
        <span
          class="text-xxs leading-3"
          :class="[checked ? 'text-dark-gray' : 'text-body-color']"
        >
          Terms
        </span>
      </div>
      <div class="margin-top-tooltip">
        <info-tooltip
          color-scheme="dark"
          content="'1'"
          class="relative"
          style="top: 1.5px"
        >
          <div>
            <p class="text-xxs">
              {{ getTextByTerm(term) }}
            </p>
          </div>
        </info-tooltip>
      </div>
    </div>
  </div>
</template>

<script>
import InfoTooltip from '@/components/InfoTooltip.vue';
export default {
  name: 'RoundRadioButtonTerms',
  components: { InfoTooltip },
  props: {
    item: String,
    name: String,
    term: String,
    checked: Boolean,
    subtext: {
      type: String,
      default: '',
    },
    customClass: {
      type: String,
      default: '',
    },
    subtextClass: {
      type: String,
      default: '',
    },
    show: {
      type: Boolean,
      default: true,
    },
    value: [String, Boolean, Number],
    handleClick: {
      type: Function,
      default: () => {},
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    apm: {
      type: [Array, Boolean],
      default: false,
    },
    highlightBox: {
      type: Boolean,
      default: false,
    },
    highlightText: {
      type: String,
      default: '',
    },
  },
  computed: {
    formatAPM() {
      if (
        !this.$store.getters['shouldEnableCHRFlow'] ||
        this.$store.getters['isForeignCarrier']
      ) {
        return this.apm.filter((m) => m !== 'load_pay');
      }
      return this.apm;
    },
  },
  methods: {
    formatAllowedPaymentMethod(apm) {
      if (apm == 'load_pay') return 'LoadPay';

      return apm?.replace(/_/g, ' ');
    },
    getTextByTerm(term) {
      const carrier = this.$store.state.carriers?.carrierDetails?.legal_name;
      const broker = this.$store.state.brokers?.logo.legal_name;
      switch (term) {
        case 'ach_deposit':
          return `${carrier} authorizes ${broker} to initiate electronic payment through the ACH system to the deposit account provided by ${carrier}.`;
        case 'quick_pay':
          return `By choosing Quick Pay, ${carrier} agrees to the terms of ${broker}'s Quick Pay program including early payment for a discount.`;
        default:
          return '';
      }
    },
  },
};
</script>

<style scoped>
/* Custom CSS for Checkboxes */
/* Template used from W3 schools https://www.w3schools.com/howto/tryit.asp?filename=tryhow_css_custom_checkbox */
/* The container */
.container {
  @apply relative block flex h-full cursor-pointer items-center pl-12 text-left text-sm text-body-color;
  min-height: 60px;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

/* Hide the browser's default checkbox */
.container input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

/* Create a custom checkbox */
.checkmark {
  @apply absolute left-2 top-2/4 h-6 w-6 rounded-full bg-white;
  transform: translate(0, -50%);
}

/* When the checkbox is checked, add a yellow background */
.container input:checked ~ .checkmark {
  @apply bg-yellow-500;
}

/* Create the checkmark/indicator (hidden when not checked) */
.checkmark:after {
  content: '';
  position: absolute;
  display: none;
}

/* Show the checkmark when checked */
.container input:checked ~ .checkmark:after {
  display: block;
}

/* Style the checkmark/indicator */
.container .checkmark:after {
  left: 8px;
  top: 8px;
  width: 8px;
  height: 8px;
  background-color: white;
  border: solid white;
  border-radius: 9999px;
}

.activeButton {
  @apply rounded-md border-2 border-yellow-500 bg-white;
  border-color: #f8c617 !important;
  background-color: white !important;
}
.margin-top-tooltip {
  margin-top: 10px;
}

.flex-at-400px {
  @apply hidden;
}
.flex-row-at-400px {
  @apply flex-col;
}
.items-center-at-400px {
  @apply items-start;
}

@media (min-width: 400px) {
  .flex-at-400px {
    @apply flex;
  }
  .flex-row-at-400px {
    @apply flex-row;
  }
  .items-center-at-400px {
    @apply items-center;
  }
}
</style>
